import dayjs, { Dayjs } from 'dayjs';
import computePortfolioPnlValues from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/computePortfolioPnlValues';
import {
	BuildPortfolioChartBaseDataParams,
	BuildPortfolioChartLiveDataParams,
} from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';

interface PnlByDay {
	pnl: number;
	feesAndFunding: number | undefined;
	isMockedSnapshot: boolean;
}

export interface CalendarPnlData {
	daysInMonth: number;
	startWeekday: number;
	pnlByDay: Map<number, PnlByDay>;
	numWinningDays: number;
	numLosingDays: number;
	numFlatDays: number;
}

interface BuildCalendarPnlBaseDataParams extends BuildPortfolioChartBaseDataParams {
	month: Dayjs;
}

export function buildCalendarPnlBaseData({
	enabled,
	snapshots,
	includeFeesAndFunding,
	realizedPnlOnly,
	month,
}: BuildCalendarPnlBaseDataParams) {
	const selectedMonth = month;
	const monthNumber = selectedMonth.month();
	const year = selectedMonth.year();
	const startOfMonth = selectedMonth.startOf('month');
	const daysInMonth = selectedMonth.daysInMonth();
	const startWeekday = startOfMonth.day();

	if (!enabled || !snapshots?.length) {
		return {
			daysInMonth,
			startWeekday,
			pnlByDay: new Map<number, PnlByDay>(),
			latestSnapshotTotalPnl: 0,
			latestSnapshotFeesAndFunding: 0,
		};
	}

	const pnlByDay = new Map<number, PnlByDay>();

	for (const [index, snapshot] of snapshots.slice(1).entries()) {
		const d = dayjs(snapshot.ts * 1000).subtract(1, 'day');

		const {
			totalPnl: currentSnapshotTotalPnl,
			feesAndFunding: currentSnapshotFeesAndFunding,
		} = computePortfolioPnlValues({
			cumulativeRealizedPnl: snapshot.cumulativeRealizedPnl,
			unrealizedPnl: snapshot.unrealizedPnl,
			cumulativeFunding: snapshot.cumulativeFunding,
			cumulativeFeePaid: snapshot.cumulativeFeePaid,
			cumulativeFeeRebate: snapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const previousSnapshot = snapshots[index];

		const {
			totalPnl: previousSnapshotTotalPnl,
			feesAndFunding: previousSnapshotFeesAndFunding,
		} = computePortfolioPnlValues({
			cumulativeRealizedPnl: previousSnapshot.cumulativeRealizedPnl,
			unrealizedPnl: previousSnapshot.unrealizedPnl,
			cumulativeFunding: previousSnapshot.cumulativeFunding,
			cumulativeFeePaid: previousSnapshot.cumulativeFeePaid,
			cumulativeFeeRebate: previousSnapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const dailyTotalPnl = computePortfolioTradeDelta(
			currentSnapshotTotalPnl,
			previousSnapshotTotalPnl
		);
		const dailyFeesAndFunding = computePortfolioTradeDelta(
			currentSnapshotFeesAndFunding,
			previousSnapshotFeesAndFunding
		);

		if (d.month() === monthNumber && d.year() === year) {
			const day = d.date();

			if (snapshot.isMockedSnapshot) {
				pnlByDay.set(day, {
					pnl: 0,
					feesAndFunding: undefined,
					isMockedSnapshot: true,
				});
				continue;
			}

			pnlByDay.set(day, {
				pnl: dailyTotalPnl,
				feesAndFunding: includeFeesAndFunding ? dailyFeesAndFunding : undefined,
				isMockedSnapshot: false,
			});
		}
	}

	const latestSnapshot = snapshots[snapshots.length - 1];

	const {
		totalPnl: latestSnapshotTotalPnl,
		feesAndFunding: latestSnapshotFeesAndFunding,
	} = computePortfolioPnlValues({
		cumulativeRealizedPnl: latestSnapshot.cumulativeRealizedPnl,
		unrealizedPnl: latestSnapshot.unrealizedPnl,
		cumulativeFunding: latestSnapshot.cumulativeFunding,
		cumulativeFeePaid: latestSnapshot.cumulativeFeePaid,
		cumulativeFeeRebate: latestSnapshot.cumulativeFeeRebate,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	return {
		daysInMonth,
		startWeekday,
		pnlByDay,
		latestSnapshotTotalPnl,
		latestSnapshotFeesAndFunding,
	};
}

interface BuildCalendarPnlLiveDataParams extends BuildPortfolioChartLiveDataParams {
	latestSnapshotTotalPnl: number;
	latestSnapshotFeesAndFunding: number;
}

export function buildCalendarPnlLiveData({
	metrics,
	currentCumulativeFunding,
	currentUnrealizedPnl,
	includeFeesAndFunding,
	realizedPnlOnly,
	enabled,
	latestSnapshotTotalPnl,
	latestSnapshotFeesAndFunding,
}: BuildCalendarPnlLiveDataParams) {
	const today = dayjs().date();

	if (!enabled) {
		return {
			day: today,
			value: {
				pnl: 0,
				feesAndFunding: undefined,
				isMockedSnapshot: false,
			},
		};
	}

	const { totalPnl: currentTotalPnl, feesAndFunding: currentFeesAndFunding } =
		computePortfolioPnlValues({
			cumulativeRealizedPnl: metrics?.cumulativeRealizedPnl,
			unrealizedPnl: currentUnrealizedPnl,
			cumulativeFunding: currentCumulativeFunding,
			cumulativeFeePaid: metrics?.cumulativeFeePaid,
			cumulativeFeeRebate: metrics?.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

	const todayTotalPnl = computePortfolioTradeDelta(
		currentTotalPnl,
		latestSnapshotTotalPnl
	);
	const todayFeesAndFunding = computePortfolioTradeDelta(
		currentFeesAndFunding,
		latestSnapshotFeesAndFunding
	);

	return {
		day: today,
		value: {
			pnl: todayTotalPnl,
			feesAndFunding: includeFeesAndFunding ? todayFeesAndFunding : undefined,
			isMockedSnapshot: false,
		},
	};
}
